import { BalanceSummary, ServiceAccount } from '../../../../apis/types'

export const PAYMENT_AMOUNT_OVER_THRESHOLD = 1.2
export const PAYMENT_AMOUNT_UNDER_THRESHOLD = 0.8

export type PaymentBalanceServiceAccount = ServiceAccount & { invoices?: { summary?: BalanceSummary } }

export function getEffectiveMaxBalance(serviceAccount: ServiceAccount): number {
    const invoice = serviceAccount.currentInvoice
    if (invoice == null) {
        return serviceAccount.billedBalanceTenantToCorp ?? 0
    }

    return Math.max(
        invoice.remainingBalance ?? 0,
        invoice.billedBalanceTenantToCorp ?? serviceAccount.billedBalanceTenantToCorp ?? 0
    )
}

export function getPaymentBalanceReferences(serviceAccount: PaymentBalanceServiceAccount): { balanceTotal: number; balanceDue?: number } {
    const summary = serviceAccount.invoices?.summary
    const effectiveMaxBalance = getEffectiveMaxBalance(serviceAccount)
    const fallbackDue =
        serviceAccount.currentInvoice?.dueBalanceTenantToCorp ?? serviceAccount.dueBalanceTenantToCorp

    const summaryTotal = summary?.balanceTotal != null ? Number(summary.balanceTotal) : NaN
    const summaryDue = summary?.balanceDue != null ? Number(summary.balanceDue) : NaN

    // Prefer positive invoice-list summary; never let a seeded $0 summary hide current-invoice balances.
    const balanceTotal = Number.isFinite(summaryTotal) && summaryTotal > 0 ? summaryTotal : effectiveMaxBalance
    const balanceDue = Number.isFinite(summaryDue) && summaryDue > 0 ? summaryDue : fallbackDue

    return { balanceTotal, balanceDue }
}

export type PaymentAmountBalanceWarningType = 'over' | 'under'

export type BalanceReferenceKind = 'total' | 'due'

export interface PaymentAmountBalanceWarningResult {
    type: PaymentAmountBalanceWarningType
    applicableReferences: BalanceReferenceKind[]
}

export function isWithinPaymentAmountTolerance(amount: number, reference: number): boolean {
    if (!Number.isFinite(reference) || reference <= 0) {
        return false
    }

    return amount >= reference * PAYMENT_AMOUNT_UNDER_THRESHOLD && amount <= reference * PAYMENT_AMOUNT_OVER_THRESHOLD
}

interface BalanceReference {
    kind: BalanceReferenceKind
    amount: number
}

function getBalanceReferences(balanceTotal: number, balanceDue?: number): BalanceReference[] {
    const references: BalanceReference[] = []

    if (Number.isFinite(balanceTotal) && balanceTotal > 0) {
        references.push({ kind: 'total', amount: balanceTotal })
    }

    const due = balanceDue ?? 0
    if (Number.isFinite(due) && due > 0 && due !== balanceTotal) {
        references.push({ kind: 'due', amount: due })
    }

    return references
}

function getApplicableReferences(amount: number, references: BalanceReference[], type: PaymentAmountBalanceWarningType): BalanceReferenceKind[] {
    const threshold = type === 'over' ? PAYMENT_AMOUNT_OVER_THRESHOLD : PAYMENT_AMOUNT_UNDER_THRESHOLD
    const isApplicable =
        type === 'over'
            ? (reference: BalanceReference) => amount > reference.amount * threshold
            : (reference: BalanceReference) => amount < reference.amount * threshold

    return references.filter(isApplicable).map((reference) => reference.kind)
}

export function getPaymentAmountBalanceWarning(
    amount: number,
    balanceTotal: number,
    balanceDue?: number
): PaymentAmountBalanceWarningResult | null {
    if (!Number.isFinite(amount) || amount <= 0) {
        return null
    }

    const references = getBalanceReferences(balanceTotal, balanceDue)
    if (references.length === 0) {
        // Paid-up / $0 references: any positive payment is outside the 80–120% band
        return { type: 'over', applicableReferences: ['total'] }
    }

    if (references.some((reference) => isWithinPaymentAmountTolerance(amount, reference.amount))) {
        return null
    }

    const exceedsAllReferences = references.every((reference) => amount > reference.amount * PAYMENT_AMOUNT_OVER_THRESHOLD)
    if (exceedsAllReferences) {
        return { type: 'over', applicableReferences: getApplicableReferences(amount, references, 'over') }
    }

    return { type: 'under', applicableReferences: getApplicableReferences(amount, references, 'under') }
}
